/**
 * Server mode: draws the replicated entities that are not mobs, items, orbs,
 * arrows, boats or players, with the renderers the offline game already has:
 *
 *   thrown   egg / snowball / pearl sprites, splash and lingering potions
 *   cloud    a lingering potion's cloud
 *   trident  a thrown trident (flying, stuck, returning)
 *   bobber   a fishing hook and its line to the owner's rod
 *   crystal, dragon, fireball   the End fight (render/entities/dragon.ts)
 *   skull    wither skulls
 *
 * Nothing here simulates: every view is the replica's (client/entities.ts),
 * one tick behind the server and interpolated by the frame's alpha.
 */

import * as THREE from 'three';
import { I } from '../game/itemIds';
import type { ThrownRenderer } from '../render/thrown';
import type { PotionRenderer } from '../render/potions';
import { BobberRenderer } from '../render/bobber';
import type { TridentRenderer } from '../render/tridentRenderer';
import { WitherSkullRenderer } from '../render/witherSkulls';
import { DragonRenderer } from '../render/entities/dragon';
import type { EnvUniforms } from '../render/env';
import { EnderDragon, type DragonEnv } from '../entities/end/dragon';
import type { DragonFight } from '../entities/end/fight';
import type { OtherView } from './entities';

export interface ProjectileDrawDeps {
  scene: THREE.Scene;
  env: EnvUniforms;
  camera: THREE.Camera;
  others(kind: string): OtherView[];
  /** Where an entity (a bobber's owner) is drawn, or null. */
  placeOf(id: number): { x: number; y: number; z: number } | null;
  /** Our own entity id. */
  self(): number;
  /** Tip of our own fishing rod. */
  rodTip(): THREE.Vector3;
  light(x: number, y: number, z: number): number;
  thrown: ThrownRenderer;
  potions: PotionRenderer;
  /** The local player's bobber renderer (it also plays the splash puffs). */
  bobber: BobberRenderer;
  tridents: TridentRenderer;
}

const SPRITES: Record<number, string> = { [I.EGG]: 'egg', [I.ENDER_PEARL]: 'ender_pearl', [I.EYE_OF_ENDER]: 'ender_eye', [I.FIREWORK_ROCKET]: 'firework_rocket' };
const num = (r: unknown, i: number): number => (Array.isArray(r) ? Number(r[i]) || 0 : 0);

type Egg = Parameters<ThrownRenderer['update']>[0][number];
type Potion = Parameters<PotionRenderer['update']>[0][number];
type Cloud = Parameters<PotionRenderer['update']>[1][number];
type Trident = Parameters<TridentRenderer['update']>[0][number];
type Hook = NonNullable<Parameters<BobberRenderer['update']>[0]>;
type Skull = Parameters<WitherSkullRenderer['update']>[0][number];

export class ProjectileDrawer {
  private readonly skulls: WitherSkullRenderer;
  private readonly end: DragonRenderer;
  /** Other players' hooks: one line renderer per bobber entity. */
  private readonly lines = new Map<number, BobberRenderer>();
  private readonly dragons = new Map<number, { d: EnderDragon; age: number }>();
  private readonly tip = new THREE.Vector3();
  /** Counts of what the last frame drew (tests read it). */
  readonly drawn = { thrown: 0, potions: 0, clouds: 0, tridents: 0, bobbers: 0, crystals: 0, dragons: 0, fireballs: 0, skulls: 0 };

  constructor(private readonly d: ProjectileDrawDeps) {
    this.skulls = new WitherSkullRenderer(d.env);
    this.end = new DragonRenderer(d.env);
    d.scene.add(this.skulls.group, this.end.group);
  }

  /** Once a frame, in place of the offline renderers' update calls. */
  frame(alpha: number, dt: number, time: number, tick: number): void {
    const d = this.d, n = this.drawn;

    // thrown items and potions
    const eggs: Egg[] = [], potions: Potion[] = [];
    for (const o of d.others('thrown')) {
      const item = num(o.record, 0);
      if (item === I.SPLASH_POTION || item === I.LINGERING_POTION) potions.push({ ...o, vx: 0, vy: 0, vz: 0, dead: false, item, dmg: num(o.record, 1) });
      else eggs.push({ ...o, vx: 0, vy: 0, vz: 0, dead: false, sprite: SPRITES[item] ?? 'egg' });
    }
    const clouds: Cloud[] = [];
    for (const o of d.others('cloud')) {
      clouds.push({ x: o.x, y: o.y, z: o.z, age: 0, life: 1, baseRadius: num(o.record, 3), color: num(o.record, 4), effects: [], cooldown: NO_COOLDOWN });
    }
    d.thrown.update(eggs, alpha, dt);
    d.potions.update(potions, clouds, alpha, dt, time);
    n.thrown = eggs.length; n.potions = potions.length; n.clouds = clouds.length;

    // tridents
    const tridents: Trident[] = [];
    for (const o of d.others('trident')) {
      tridents.push({ x: o.x, y: o.y, z: o.z, px: o.px, py: o.py, pz: o.pz, yaw: o.yaw, pitch: o.pitch, light: d.light(o.x, o.y + 0.5, o.z), enchanted: num(o.record, 7) === 1 });
    }
    d.tridents.update(tridents, alpha);
    n.tridents = tridents.length;

    // bobbers: ours hangs from our rod, the others from their owner's hand
    let mine: Hook | null = null;
    const seen = new Set<number>();
    const self = d.self();
    const hooks = d.others('bobber');
    for (const o of hooks) {
      const owner = num(o.record, 3);
      const hook = o as unknown as Hook; // the renderer reads x, y, z and their previous values only
      if (owner === self) { mine = hook; continue; }
      const at = d.placeOf(owner);
      if (!at) continue;
      seen.add(o.id);
      let r = this.lines.get(o.id);
      if (!r) { r = new BobberRenderer(); d.scene.add(r.group); this.lines.set(o.id, r); }
      r.update(hook, alpha, dt, this.tip.set(at.x, at.y + 1.25, at.z));
    }
    for (const [id, r] of this.lines) {
      if (seen.has(id)) continue;
      r.update(null, alpha, dt, this.tip);
      r.group.removeFromParent();
      this.lines.delete(id);
    }
    d.bobber.update(mine, alpha, dt, d.rodTip());
    n.bobbers = hooks.length;

    // wither skulls
    const skulls: Skull[] = [];
    for (const o of d.others('skull')) {
      skulls.push({ ...o, blue: num(o.record, 0) === 1, vx: num(o.record, 4), vy: num(o.record, 5), vz: num(o.record, 6), ax: 0, ay: 0, az: 0 } as unknown as Skull);
    }
    this.skulls.update(skulls, alpha);
    n.skulls = skulls.length;

    // the End fight: crystals (with the healing beam), the dragon, its fireballs
    const crystals = d.others('crystal').map((o) => ({ id: o.id, pillar: 0, x: o.x, y: o.y, z: o.z, alive: true, beam: num(o.record, 3) === 1, age: o.age }));
    const fireballs = d.others('fireball').map((o) => ({ ...o, vx: 0, vy: 0, vz: 0, removed: false }));
    let dragon: EnderDragon | null = null;
    const live = new Set<number>();
    for (const o of d.others('dragon')) {
      live.add(o.id);
      let e = this.dragons.get(o.id);
      if (!e) {
        e = { d: new EnderDragon(NO_ENV, { pillars: [], exit: { x: 0, y: 64, z: 0 } }, o.x, o.y, o.z), age: -1 };
        e.d.yaw = e.d.pyaw = o.yaw;
        this.dragons.set(o.id, e);
      }
      // one step per replica tick
      if (e.age !== o.age) { e.age = o.age; e.d.follow(o.x, o.y, o.z, o.yaw, o.limb, o.hurtTime, o.deathTime); e.d.health = num(o.record, 4); }
      dragon = e.d;
    }
    for (const id of this.dragons.keys()) if (!live.has(id)) this.dragons.delete(id);
    this.end.update({ dragon, crystals, fireballs, clouds: [] } as unknown as DragonFight, alpha, d.camera, tick);
    n.crystals = crystals.length; n.dragons = live.size; n.fireballs = fireballs.length;
  }
}

const NO_COOLDOWN = new Map<string, number>();
/** The replica dragon is never ticked, so its world is never asked anything. */
const NO_ENV = {} as DragonEnv;
